import { createContext, useContext } from 'react'

import type {
  EmbroideryProviderEmbroideryVariantFieldsFragment,
  ProductCatalogEmbroideryTypes,
} from '../../../__generated__/graphql/catalog/graphql'
import type { Props as B2bLogoSneakPeekProps } from '../../../components/embroidery/EmbroiderySneakPeek/B2bLogoSneakPeek'
import type { Props as DtcLogoSneakPeekProps } from '../../../components/embroidery/EmbroiderySneakPeek/DTCLogoSneakpeek'
import type { Props as IconSneakPeekProps } from '../../../components/embroidery/EmbroiderySneakPeek/IconSneakPeek'
import type { Props as TextSneakPeekProps } from '../../../components/embroidery/EmbroiderySneakPeek/TextSneakPeek'
import type { EmbroideryItemCustomProperties } from '../types'
import type { getEmbroideryPriceRange } from '../utils/getEmbroideryPriceRange'

/** The form kinds the embroidery modal is organized around. */
export type ExternalEmbroideryFormKind = 'TEXT' | 'ICON' | 'LOGO_B2B' | 'LOGO_SETUP' | 'LOGO_DTC'

export type EmbroideryPriceRange = ReturnType<typeof getEmbroideryPriceRange>

/** The checkbox beside one sneak peek, letting a shopper leave its embroidery out of the bag. */
export type EmbroiderySummaryCheckbox = {
  label: string
  checked: boolean
  disabledByConflict: boolean
  onToggle: () => void
}

/**
 * One sneak peek on the summary card. `id` keys the row and names its checkbox: the kind on the
 * legacy card, the item's instance id on the multi-placement card. `checkbox` is absent when the
 * shopper cannot leave the embroidery out.
 */
export type EmbroiderySummarySneakPeek = { id: string; checkbox?: EmbroiderySummaryCheckbox } & (
  | { kind: 'TEXT'; props: TextSneakPeekProps }
  | { kind: 'ICON'; props: IconSneakPeekProps }
  | { kind: 'LOGO_B2B'; props: B2bLogoSneakPeekProps }
  | { kind: 'LOGO_DTC'; props: DtcLogoSneakPeekProps }
)

/** Everything the summary card prints, resolved: prices formatted, labels translated. */
export type EmbroiderySummaryView = {
  editEmbroideryLabel: string
  editEmbroideryText: string
  /** Formatted and ready to print, or absent where this surface shows no prices. */
  price: string | undefined
  toggleAddEmbroideryText: string
  /** In display order, one per chosen kind (legacy) or per item (multi-placement). */
  sneakPeeks: readonly EmbroiderySummarySneakPeek[]
}

/**
 * What a product page needs from embroidery, whichever stack is serving it: the section, the
 * CTA, the summary card, and the cart maker read only these fields.
 */
export type ExternalEmbroideryFacade = {
  /** The embroidery types this garment supports. */
  productEmbroideryOptionKeys: ProductCatalogEmbroideryTypes[]
  /**
   * Whether this garment offers any embroidery at all. Read from the config the provider was
   * given, which a flow like gifting may have narrowed from the catalog's.
   */
  isProductEmbroiderable: boolean
  /** Types the flow makes mandatory, so the shopper cannot leave them out. Empty for DTC. */
  requiredEmbroideryTypes: ProductCatalogEmbroideryTypes[]
  /** What embroidery could cost on this garment, before anything is chosen. */
  embroideryPriceRange: EmbroideryPriceRange | undefined
  /** Whether this is a business flow. */
  isB2bEmbroidery: boolean

  /** Whether the account has embroidery preferences that apply here. */
  savedEmbroideryPreferences: object | null
  isLoadingSavedEmbroideryPreferences: boolean

  /** Whether the shopper has been through the modal and accepted what they chose. */
  confirmAddEmbroidery: boolean
  /** Whether there is any embroidery to show, chosen or preferred. */
  hasEmbroidery: boolean
  hasB2bLogoSelection: boolean
  embroiderySummary: EmbroiderySummaryView
  /** Reasons the page cannot be added to the bag yet. Empty in the ordinary case. */
  embroiderySelectionErrorMessages: string[]

  /** The embroidery product the bag will buy, once something is chosen. */
  embroideryItemVariant: EmbroideryProviderEmbroideryVariantFieldsFragment | undefined
  /** The digitization product, when the chosen logo has not been digitized yet. */
  embroiderySetupVariant: EmbroideryProviderEmbroideryVariantFieldsFragment | undefined
  /** The chosen embroidery in cart wire format. */
  embroideryLineItemProperties: EmbroideryItemCustomProperties | undefined

  /** Opens the modal on one kind, or clears the choice of kind. */
  setSelectedFormKind: (formKind: ExternalEmbroideryFormKind | null) => void
  /** Opens the modal, on the kind `setSelectedFormKind` last chose if it chose one. */
  openEmbroideryModal: () => void
}

const ExternalEmbroideryContext = createContext<ExternalEmbroideryFacade | undefined>(undefined)

export const ExternalEmbroideryFacadeProvider = ExternalEmbroideryContext.Provider

/**
 * Reads whichever stack is serving this page.
 *
 * Throws rather than returning a default: a part that reads embroidery outside an embroidery
 * provider is a mount mistake, and a default would hide it behind an empty summary card.
 */
export const useExternalEmbroidery = (): ExternalEmbroideryFacade => {
  const facade = useContext(ExternalEmbroideryContext)
  if (!facade) {
    throw new Error('useExternalEmbroidery must be used within an embroidery provider')
  }
  return facade
}
